import type { User } from '../types';

/**
 * The wire shape of the `user` object returned by the session endpoints
 * (`/auth/login`, `/auth/refresh`, `/auth/assume`). It uses `userType` (not the
 * web model's `type`) and — critically — omits `channelIds`, which the API stamps
 * into the token as a login-time snapshot but does not resend on refresh.
 */
export interface SessionUserWire {
  id: string;
  email: string;
  displayName: string;
  userType: number;
  tenantId: string | null;
  tenantName: string | null;
  roles: string[];
  permissions: string[];
  channelIds?: string[];
}

/**
 * Map a session wire user to the web `User` model.
 *
 * Every place that writes a session into `authStore` MUST go through here.
 * Feeding the raw wire object straight to `setVSNAuth` (as the refresh paths
 * used to) leaves `user.type` undefined — because the wire field is `userType` —
 * which silently degrades a ChannelAdmin after the first token refresh: the
 * tenant picker (rendered only for type 1/5) and the role label both vanish.
 *
 * `channelIds` is display-only and absent from refresh responses, so preserve
 * the value captured at login across a refresh — otherwise a ChannelAdmin's
 * channel context disappears the moment the token rotates.
 */
export function toSessionUser(wire: SessionUserWire, previous?: User | null): User {
  return {
    id: wire.id,
    email: wire.email,
    displayName: wire.displayName,
    type: wire.userType as 1 | 2 | 3 | 5,
    tenantId: wire.tenantId,
    tenantName: wire.tenantName,
    roles: wire.roles,
    permissions: wire.permissions,
    channelIds: wire.channelIds ?? previous?.channelIds,
  };
}
